import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';

const Settings = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [announcement, setAnnouncement] = useState({
    text: '',
    image_url: '',
    image_data: '',
    is_active: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const response = await api.get('/announcement');
      setAnnouncement({
        text: response.data.text || '',
        image_url: response.data.image_url || '',
        image_data: response.data.image_data || '',
        is_active: response.data.is_active || false
      });
    } catch (error) {
      console.error('Error fetching announcement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAnnouncement({ 
        ...announcement, 
        image_data: reader.result,
        image_url: '' // Clear URL when uploading
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setAnnouncement({ 
      ...announcement, 
      image_data: '',
      image_url: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put(`/admin/announcement?user_id=${user?.id}`, announcement);
      setMessage({ type: 'success', text: 'Announcement saved successfully!' });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save announcement' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const currentImage = announcement.image_data || announcement.image_url;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-primary mb-6">Site Settings</h1>

        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}>
            <p className="text-white">{message.text}</p>
          </div>
        )}

        {/* Announcement Section */}
        <div className="bg-dark-secondary rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Announcement Image</h2>
          <p className="text-gray-400 mb-6">
            This image appears on the Home page above the weeks section.
          </p>

          {/* Active Toggle */}
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={announcement.is_active}
                onChange={(e) => setAnnouncement({ ...announcement, is_active: e.target.checked })}
                className="sr-only"
              />
              <div className={`relative w-14 h-8 rounded-full transition-colors ${
                announcement.is_active ? 'bg-primary' : 'bg-gray-600'
              }`}>
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  announcement.is_active ? 'translate-x-6' : ''
                }`}></div>
              </div>
              <span className="ml-3 text-white font-semibold">
                {announcement.is_active ? 'Active' : 'Inactive'}
              </span>
            </label>
          </div>

          {/* Image Upload */}
          <div className="mb-6">
            <label className="block text-gray-300 mb-2">Upload Image</label>
            <div className="flex flex-col gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <div className="flex gap-3">
                <label
                  htmlFor="image-upload"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-80 transition cursor-pointer inline-block"
                >
                  Choose Image
                </label>
                {currentImage && (
                  <button
                    onClick={handleRemoveImage}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    Remove Image
                  </button>
                )}
              </div>
              <p className="text-gray-500 text-sm">Max file size: 5MB. Supports JPG, PNG, GIF, WebP.</p>
            </div>
          </div>

          {/* Image Preview */}
          {currentImage && (
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Image Preview</label>
              <div className="bg-dark rounded-lg p-4">
                <img 
                  src={currentImage} 
                  alt="Preview" 
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Optional Text */}
          <div className="mb-6">
            <label className="block text-gray-300 mb-2">Optional Text (appears below image)</label>
            <textarea
              value={announcement.text}
              onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })}
              placeholder="Enter optional text here..."
              rows={3}
              className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-80 transition disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
